import { useState } from 'react';
import { Alert, Box, Group, Loader, Stack, Text, Textarea, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { Button } from 'src/components';
import { ApiError } from 'src/error/ApiError';
import { useMicrosoftEmailConnectionStatus } from 'src/common/hooks/useMicrosoftEmailConnectionStatus';
import { useStartMicrosoftEmailOAuthMutation } from 'src/common/hooks/useStartMicrosoftEmailOAuthMutation';
import { OutlookDisconnectButton } from 'src/common/components/OutlookDisconnectButton';
import { useSendMicrosoftEmailMutation } from 'src/secret-menu/microsoft-email/hooks/useSendMicrosoftEmailMutation';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function MicrosoftEmailTab() {
  const {
    connected,
    isLoading: isLoadingStatus,
    isError: isStatusError,
    refetch: refetchStatus,
  } = useMicrosoftEmailConnectionStatus();
  const { startOAuth, isLoading: isStarting } = useStartMicrosoftEmailOAuthMutation();
  const { sendEmail, isLoading: isSending } = useSendMicrosoftEmailMutation();

  const [recipient, setRecipient] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [validationErrors, setValidationErrors] = useState<{
    recipient?: string;
    subject?: string;
    body?: string;
  }>({});
  const [needsReconnect, setNeedsReconnect] = useState(false);

  const handleStartAuth = async () => {
    try {
      const { authUrl } = await startOAuth();
      window.location.href = authUrl;
    } catch (error) {
      setNeedsReconnect(false);
      notifyAndLogError('Failed to connect Microsoft account', 'Please try again later.', error);
    }
  };

  const validate = () => {
    const errors: { recipient?: string; subject?: string; body?: string } = {};

    if (!recipient.trim()) {
      errors.recipient = 'Recipient is required';
    } else if (!EMAIL_REGEX.test(recipient.trim())) {
      errors.recipient = 'Please enter a valid email address';
    }

    if (!subject.trim()) {
      errors.subject = 'Subject is required';
    }

    if (!body.trim()) {
      errors.body = 'Body is required';
    }

    return errors;
  };

  const handleSend = async () => {
    const errors = validate();
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      await sendEmail({ recipient: recipient.trim(), subject: subject.trim(), body: body.trim() });
      notifications.show({
        title: 'Email sent',
        message: 'Your email was sent successfully.',
        color: 'green',
      });
      setRecipient('');
      setSubject('');
      setBody('');
      setValidationErrors({});
      setNeedsReconnect(false);
    } catch (error) {
      if (error instanceof ApiError && error.status === 424) {
        setNeedsReconnect(true);
        return;
      }
      notifyAndLogError('Failed to send email', 'Please try again later.', error);
    }
  };

  if (isLoadingStatus) {
    return <Loader />;
  }

  if (isStatusError) {
    return (
      <Box p="md">
        <Stack gap="md">
          <Text c="red">Error loading connection status</Text>
          <Button onClick={() => refetchStatus()} size="sm">
            Try again
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!connected) {
    return (
      <Box p="md">
        <Stack gap="md">
          <Title order={2}>Microsoft email</Title>
          <Text>Connect your Microsoft account to send emails.</Text>
          <Button onClick={handleStartAuth} loading={isStarting}>
            Connect Microsoft account
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box p="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={2}>Microsoft email</Title>
          <OutlookDisconnectButton variant="default" size="sm" />
        </Group>
        <Text size="sm" c="dimmed">
          Connected. Disconnect to reconnect with a different account.
        </Text>

        {needsReconnect && (
          <Alert color="orange" title="Reconnection required">
            <Stack gap="sm">
              <Text size="sm">
                Your Microsoft account connection has expired. Please reconnect to send emails.
              </Text>
              <Button onClick={handleStartAuth} loading={isStarting} size="sm">
                Reconnect Microsoft account
              </Button>
            </Stack>
          </Alert>
        )}

        <TextInput
          label="Recipient"
          placeholder="recipient@example.com"
          value={recipient}
          onChange={e => {
            setRecipient(e.currentTarget.value);
            if (validationErrors.recipient) {
              setValidationErrors(prev => ({ ...prev, recipient: undefined }));
            }
          }}
          error={validationErrors.recipient}
          required
        />

        <TextInput
          label="Subject"
          placeholder="Email subject"
          value={subject}
          onChange={e => {
            setSubject(e.currentTarget.value);
            if (validationErrors.subject) {
              setValidationErrors(prev => ({ ...prev, subject: undefined }));
            }
          }}
          error={validationErrors.subject}
          required
        />

        <Textarea
          label="Body"
          placeholder="Email body"
          value={body}
          onChange={e => {
            setBody(e.currentTarget.value);
            if (validationErrors.body) {
              setValidationErrors(prev => ({ ...prev, body: undefined }));
            }
          }}
          error={validationErrors.body}
          required
          minRows={6}
          autosize
        />

        <Button onClick={handleSend} loading={isSending} disabled={isSending || needsReconnect}>
          Send
        </Button>
      </Stack>
    </Box>
  );
}
